import {
    CREATE_FORUM_OR_GROUP,
    GROUP_LIST,
    LIST_OF_FORUMS,
    SEO_DESCRIPTION_FORUM_category,
    SEO_TITLE_FORUM_TOPICS,
} from 'mk/autogenerated/translations/ListOfForumsPage.d0a686696e6975fdcb43ceff9b6f39d1'
import { analyticsStoreEvent, toastAdd } from 'mk2/actions';
import { ListOfForumsRight } from 'mk2/apps/forum/containers/ListOfForums/ListOfForumsRight';
import { topicUrl } from 'mk2/apps/forum/helpers';
import { CategoryEntity, TopicEntity, TopicSchema } from 'mk2/apps/forum/schemas';
import { forumListOfForumsUrl, newForumOrGroupExplanationUrl } from 'mk2/apps/forum/urls';
import { groupsListOfGroupsUrl } from 'mk2/apps/groups/urls';
import Btn, { BtnType } from 'mk2/components/Btn';
import ServerErrorToast from 'mk2/components/toasts/ServerErrorToast';
import { AppName } from 'mk2/containers/Application/Application';
import { cacheLast, memoizeOne } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { BrandingProps, HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import {
    AppState,
    getRequestBaseUrl,
    getRequestPermissions,
    getRequestUser,
    getResponseRenderedAt,
} from 'mk2/reducers';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { MenuSelection, UserEntity } from 'mk2/schemas';
import { getDenormalizedEntities, getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import { parse } from 'query-string';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import ListOfForums from './ListOfForums';
import { listOfForumsLoad } from './ListOfForums.actions';
import listOfForumsPageReducer, { ListOfForumsPageState, TopicsListState } from './ListOfForums.reducers';
import listOfForumsSaga from './ListOfForums.sagas';
import { getListOfForumsState, getListOfForumsTopicsListState } from './ListOfForums.selectors';

type ItemType = 'category' | 'topic';

interface Item {
    type: ItemType;
    topicId: number;
    category?: CategoryEntity;
    topic?: TopicEntity;
}

interface OwnProps {
    className?: string;

    // You can change list item to="" link
    itemToTopicUrl(item: TopicEntity);
}

interface StateProps {
    after: number;
    baseUrl: string;
    brandDaysRowNo: number;
    contestsRowNo: number;
    contestsTopic: TopicEntity;
    errorMessage: string;
    hasNext: number;
    hasPrevious: number;
    items: Item[];
    loadingState: LoadingState;
    renderedAt: number;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    systemCatRowNo: number;
    testingsRowNo: number;
    testingsTopic: TopicEntity;
}

interface DispatchProps {
    onLoad(username: string, after?: number);
    onStoreEvent(name: string, props: any);

    onFailureToast();
}

const topicsToItems = memoizeOne((topics: TopicEntity[]): Item[] => {
    let lastCategoryId = null;
    return topics.reduce((acc, topic) => {

        // category heading
        if (topic.category.id !== lastCategoryId) {
            lastCategoryId = topic.category.id;
            acc.push({
                type: 'category',
                category: topic.category,
                topicId: topic.id,
            });
        }

        // topic
        acc.push({
            type: 'topic',
            topic,
            topicId: topic.id,
        });
        return acc;

    }, []);
});

type RouteProps = RouteComponentProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ListOfForumsPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {

    private cacheHeaderRightButton = cacheLast();
    private cacheHeaderTabsButton = cacheLast();
    private menuSelection = {
        activeSection: 'forum',
        activeItem: 'list',
    };

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsed = parse(ownProps.location.search);
            const after = parsed.after;

            const pageState: ListOfForumsPageState = getListOfForumsState(state);
            const listState: TopicsListState = getListOfForumsTopicsListState(state);

            const topics = getDenormalizedEntities<TopicEntity>(state, TopicSchema, listState.ids);
            const hasNext = listState.hasNext;

            const requestUser = getRequestUser(state);
            const requestUserPermissions = getRequestPermissions(state);

            const items = topicsToItems(topics);

            const systemCatRowNo = (!hasNext && isSuccess(listState.loadingState)) ? items.length : null;
            const contestsRowNo = systemCatRowNo !== null ? systemCatRowNo + 1 : null;
            const testingsRowNo = systemCatRowNo !== null ? systemCatRowNo + 2 : null;
            const brandDaysRowNo = systemCatRowNo !== null ? systemCatRowNo + 3 : null;

            return {
                after,
                baseUrl: getRequestBaseUrl(state),
                brandDaysRowNo,
                contestsRowNo,
                contestsTopic: getDenormalizedEntity<TopicEntity>(state, TopicSchema, pageState.contestsTopicId),
                errorMessage: listState.errorMessage,
                hasNext,
                hasPrevious: listState.hasPrevious,
                items,
                loadingState: listState.loadingState,
                renderedAt: getResponseRenderedAt(state),
                requestUser,
                requestUserPermissions,
                systemCatRowNo,
                testingsRowNo,
                testingsTopic: getDenormalizedEntity<TopicEntity>(state, TopicSchema, pageState.testingsTopicId),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: listOfForumsLoad,
            onStoreEvent: analyticsStoreEvent,

            onFailureToast: () => toastAdd('error', {
                component: ServerErrorToast,
            }),
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FORUM_TOPICS;
    }

    public getMetaDescription(props: Props): string {
        return interpolate(SEO_DESCRIPTION_FORUM_category, { category: '...' });
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + url(forumListOfForumsUrl, {});
    }

    public render(props: Props): false | JSX.Element {
        return (
            <ListOfForums
                {...props}
                itemToTopicUrl={this.itemToTopic}
            />
        );
    }

    public renderRight(props: Props): JSX.Element {
        return (
            <ListOfForumsRight {...props} />
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderTabs(props: Props): (JSX.Element[] | null) {
        const { match: { params } } = props;
        return this.cacheHeaderTabsButton([(
            <Btn
                key="forums"
                type={BtnType.Tab}
                label={LIST_OF_FORUMS}
                link={{
                    pathname: url(forumListOfForumsUrl, { ...params }),
                    search: props.location.search,
                }}
                active
            />
        ), (
            <Btn
                key="groups"
                type={BtnType.Tab}
                label={GROUP_LIST}
                link={url(groupsListOfGroupsUrl, {})}
            />
        )], props.location.search);
    }

    public getHeaderRight(props: Props): React.ReactNode {
        return this.cacheHeaderRightButton(
            <Btn
                type={BtnType.Blue}
                label={CREATE_FORUM_OR_GROUP}
                link={newForumOrGroupExplanationUrl}
            />,
        );
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: 'Forum Category - Halfpage Sticky (Desktop)',
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: 'Forum - Branding',
        };
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return this.menuSelection;
    }

    public hasMergedForumAndGroups(props: Props): boolean {
        return true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.forum.listOfForums', listOfForumsPageReducer);
        store.injectSaga('containers.forum.listOfForums', listOfForumsSaga);
    }

    private itemToTopic = (item: TopicEntity) => topicUrl(item);
}
